import React, { useCallback, useEffect, useState } from 'react';
import styles from './style/Dashboard.module.scss';
import Modal from '../Utilities/Modal';
import Table from '../Utilities/Table';
import Button from '../Utilities/Button';
import Paginate from '../Utilities/Paginate';
import { MdAdminPanelSettings, MdClear } from 'react-icons/md';
import EmptyBox from '../Utilities/EmptyBox';
import { useDispatch, useSelector } from 'react-redux';
import LoadingTable from '../Utilities/LoadingTable';
import Tabs from '../Utilities/Tabs';
import InputSearch from '../Utilities/InputSearch';
import ToggleSwitch from '../Utilities/ToggleSwitch';
import { getEnterpriseUsers, selectEnterprise } from '../../store/slice/enterpriseLists';
import { BsCheckCircleFill } from 'react-icons/bs';
import { selectPermissionSettings } from '../../store/slice/permissionSettings';
import ServiceChip from '../Utilities/ServiceChip';

const ModalUsers = ({ isOpen, setIsOpen, enterpriseId, setEnterpriseId, serviceId, setServiceId }) => {
  const headerData = [
    {
      title: '姓名',
      width: 15,
    },
    {
      title: 'Email',
      width: 20,
    },
    {
      title: '服務',
      width: 22,
    },
    {
      title: '加入時間',
      width: 10,
    },
    {
      title: '驗證狀態',
      width: 10,
      align: 'center',
    },
    {
      title: '帳號開通',
      width: 12,
      align: 'center',
    },
    {
      title: '企業管理者',
      width: 8,
      align: 'center',
    },
  ];

  const dispatch = useDispatch();
  const { management_users } = useSelector(selectEnterprise);
  const { auth } = useSelector(selectPermissionSettings);
  const [page, setPage] = useState(1);
  const [tabValue, setTabValue] = useState(0);
  const [search, setSearch] = useState('');
  const [searchValue, setSearchValue] = useState('');
  // 骨架用 local flag，不讀 redux 的 loading：
  // 1. 這張表的資料來自 enterpriseLists.getEnterpriseUsers，但 redux loading 是跨頁共用的，
  //    會被父頁面自己的請求牽動，跟這張表的實際狀態無關。
  // 2. management_users 是全域的、關閉 modal 不會清空，換一家企業開啟時會先閃出上一家的
  //    使用者列表；掛載時只要有 enterpriseId 就先進骨架，不要閃舊資料或空狀態。
  //    （沒有 enterpriseId 就不會發請求，此時給 false 才不會卡在骨架。）
  const [isFetching, setIsFetching] = useState(Boolean(enterpriseId));
  const limit = 10;

  const fetchUsers = useCallback(
    (targetPage) => {
      setIsFetching(true);
      const postObj = {
        enterprise_id: enterpriseId,
        page: targetPage,
        limit: limit,
        search: searchValue,
        has_service: serviceId,
        ...(tabValue > 0 ? { enable: tabValue === 2 ? '1' : '0' } : {}),
      };
      return dispatch(getEnterpriseUsers(postObj)).finally(() => setIsFetching(false));
    },
    [dispatch, enterpriseId, searchValue, serviceId, tabValue]
  );

  useEffect(() => {
    if (enterpriseId) {
      setPage(1);
      fetchUsers(1);
    }
  }, [enterpriseId, fetchUsers]);

  const pageChangeHandler = (e) => {
    setPage(parseInt(e.selected + 1));
    fetchUsers(e.selected + 1);
  };

  const [tabList] = useState(['全部', '未啟用', '已啟用']);

  return (
    <Modal isOpen={isOpen} size='xxl' maxHeight='80vh'>
      <div className={styles.ModalContainer}>
        <div className={`${styles.modalHeader} ${styles.special}`}>
          <div className={styles.modalTitle}>使用者名單</div>
          <Button
            type='tertiary'
            icon={<MdClear />}
            onClick={() => {
              setIsOpen(false);
              setEnterpriseId('');
              setPage(1);
              setServiceId('');
            }}
          />
        </div>

        <div className={`${styles.modalHeader} ${styles.special}`}>
          <div className={styles.modalWrapper}>
            {!serviceId && (
              <Tabs values={tabList} onChange={(e) => setTabValue(e.tabIndex)} selectedIndex={tabValue} />
            )}
          </div>

          <InputSearch
            placeholder='搜尋使用者姓名或Email'
            width={'200px'}
            value={search}
            onKeyPress={(e) => (e.code === 'Enter' || e.charCode === 13) && setSearchValue(search)}
            onChange={(e) => setSearch(e.target.value)}
            clickSearchIcon={() => {
              setSearchValue(search);
              setPage(1);
            }}
            onClear={() => {
              setSearch('');
              setSearchValue('');
            }}
          />
        </div>

        <div className={`${styles.modalBody} ${styles.nonOverflowPlus}`}>
          <Table header={headerData}>
            {isFetching ? (
              <LoadingTable row={limit} widths={headerData.map((item) => item.width)} />
            ) : (
              <>
                {management_users?.users?.length > 0 ? (
                  <tbody>
                    {management_users?.users?.map((data, index) => (
                      <tr key={index}>
                        <td width={`${headerData[0].width}%`} className={styles.textPrimary}>
                          {data.name}
                        </td>
                        <td width={`${headerData[1].width}%`} className={styles.textPrimary}>
                          {data.email}
                        </td>
                        <td width={`${headerData[2].width}%`} className={styles.textPrimary}>
                          {(serviceId ? data.services?.filter((itm) => itm.id === serviceId) : data.services)?.map((item) => (
                            <ServiceChip key={item.id} name={item?.name} />
                          ))}
                        </td>
                        <td width={`${headerData[3].width}%`}>{data.email_verified_at}</td>
                        <td width={`${headerData[4].width}%`} className={styles.textCenter}>
                          {data.email_verified_at && (
                            <div className={styles.checkIcon}>
                              <BsCheckCircleFill />
                            </div>
                          )}
                        </td>
                        <td width={`${headerData[5].width}%`} style={{ textAlign: 'center' }}>
                          <ToggleSwitch isChecked={data.enable} disabled={auth?.roles.find((item) => item === 'workspace.admin') ? false : true} small />
                        </td>
                        <td width={`${headerData[6].width}%`} className={styles.textPrimary} style={{ textAlign: 'center' }}>
                          {data.roles?.find((item) => item === 'cloud.admin') && <MdAdminPanelSettings style={{ fontSize: '22px' }} />}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                ) : (
                  <tbody style={{ height: '450px' }}>
                    <tr>
                      <td colSpan={headerData.length} style={{ width: '100%', textAlign: 'center' }}>
                        <EmptyBox type={1} caption={'目前還未有使用者名單資料'} />
                      </td>
                    </tr>
                  </tbody>
                )}
              </>
            )}
          </Table>
        </div>

        {/* 載入中不顯示分頁器，否則會先掛著上一次查詢的頁數與總數 */}
        {!isFetching && management_users?.users?.length > 0 && (
          <div className={`${styles.modalFooter} ${styles.normal}`}>
            <Paginate
              onPageChange={pageChangeHandler}
              pageCount={page}
              limit={limit}
              totalPages={Math.ceil(management_users?.users_count / limit)}
              totalData={management_users?.users_count}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ModalUsers;
